import type { PlanProjection } from '@developerz/domain';
import { A } from '@solidjs/router';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { copyText } from '../lib/clipboard';
import { shortPlanId } from '../lib/custom-plans';
import { orgHref } from '../lib/org';
import { usePlan } from '../lib/plan';
import { PlanDagRail } from './PlanDagRail';
import { PlanGroupTable } from './PlanGroupTable';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';
import { Button, buttonClass } from './ui/Button';

// The "Custom plans" section's row expansion (#4341) — DAG rail (2+ groups
// only) plus the group table, over ONE plan's own `GET /v1/plans/:planId`
// read. Container: this is the only file in the split that fetches; both
// `<PlanDagRail/>` and `<PlanGroupTable/>` are pure presenters over the
// groups this hands them.
//
// OWNS ITS OWN <QueryBoundary/> so a failing detail leaves the surrounding
// list intact — the section renders one row per plan from `useCustomPlans`,
// and a 404/500 on ONE expanded plan's detail must not blank the rows around
// it. Mirrors `EpicView.tsx`'s shape one level down: same query, same
// boundary, no route/params here since the plan id arrives as a prop instead
// of a URL segment.
//
// THE RAIL RENDERS ONLY AT 2+ GROUPS — the section's own acceptance
// criterion, not a rule this component derives: 77% of plans have exactly
// one group, and a one-node rail is noise beside the table that already
// shows that single row.
//
// "OPEN FULL VIEW" LIVES HERE, not on `<CustomPlanRow/>` — that row is
// itself a `<button>` (Up/Down/Enter walk target in
// `CustomPlansSection.tsx`), and nesting an `<a>` inside a `<button>` is
// invalid content model on top of failing `expectAxeClean`'s
// `nested-interactive` rule. This is the read-only breakdown's own door out
// to the write surface (`EpicView.tsx`, which alone carries `<StopPlanModal/>`)
// — same `orgHref`/`buttonClass` pattern `PlanCard.tsx` already uses for the
// same destination.
//
// THE PLAN-ID COPY CONTROL LIVES HERE TOO, for the same nested-interactive
// reason — the issue's own "Row:" bullet gives the row hover-only (`title=`)
// and reserves click-driven copy for "Breakdown:". Same idiom as
// `PlanGroupTable.tsx`'s per-task copy button: one signal tracks whichever
// id was just copied, `copyText` (`lib/clipboard.ts`) answers whether it
// actually landed, and the button's own label flips to "Copied" rather than
// a separate toast.

export type PlanBreakdownProps = {
  readonly planId: string;
  readonly token: string | null;
};

export function PlanBreakdown(props: PlanBreakdownProps): JSX.Element {
  const plan = usePlan(() => props.planId, props.token);
  const [copied, setCopied] = createSignal(false);
  const copyPlanId = (): void => {
    void copyText(props.planId).then(setCopied);
  };

  return (
    <QueryBoundary query={plan} loading={<Skeleton height="8rem" class="custom-plan-breakdown" />}>
      {(projection: PlanProjection) => (
        <div class="custom-plan-breakdown">
          <div class="custom-plan-breakdown__actions">
            <span class="custom-plan-breakdown__plan-id">
              <code title={props.planId}>{shortPlanId(props.planId)}</code>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('copy_snippet.aria', { label: t('fleet.plans.custom.plan_id') })}
                onClick={copyPlanId}
              >
                {copied() ? t('copy_snippet.copied') : t('copy_snippet.copy')}
              </Button>
            </span>
            <A
              class={buttonClass({ variant: 'outline', size: 'sm' })}
              href={orgHref(`/fleet/plans/${encodeURIComponent(props.planId)}`)}
            >
              {t('fleet.plans.custom.open_full')}
            </A>
          </div>
          <Show when={projection.groups.length >= 2}>
            <PlanDagRail groups={projection.groups} />
          </Show>
          <PlanGroupTable groups={projection.groups} />
        </div>
      )}
    </QueryBoundary>
  );
}
